/**
 * Firebase Authentication Service
 * Handles phone OTP authentication
 */
import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import { auth } from '@/config/firebase.config';

// Store confirmation result for OTP verification
let confirmationResult: ConfirmationResult | null = null;
let recaptchaVerifier: RecaptchaVerifier | null = null;

/**
 * Get or create invisible reCAPTCHA verifier
 */
const getRecaptchaVerifier = (): RecaptchaVerifier => {
  if (!recaptchaVerifier) {
    let container = document.getElementById('recaptcha-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'recaptcha-container';
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      document.body.appendChild(container);
    }

    recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'invisible',
      callback: () => {},
    });
  }
  return recaptchaVerifier;
};

/**
 * Clear reCAPTCHA verifier and its DOM container
 */
const clearRecaptchaVerifier = (): void => {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch (e) {
      // Ignore errors during cleanup
    }
    recaptchaVerifier = null;
  }
  const container = document.getElementById('recaptcha-container');
  if (container) {
    container.remove();
  }
};

/**
 * Send OTP to phone number
 * @param phoneNumber - Phone number with country code (e.g., +919876543210)
 * @returns Verification ID for OTP verification
 */
export const sendOTP = async (phoneNumber: string): Promise<string> => {
  try {
    const formattedPhone = phoneNumber.startsWith('+') 
      ? phoneNumber 
      : `+91${phoneNumber}`;

    clearRecaptchaVerifier();
    const verifier = getRecaptchaVerifier();

    confirmationResult = await signInWithPhoneNumber(
      auth,
      formattedPhone,
      verifier
    );

    return confirmationResult.verificationId;
  } catch (error: unknown) {
    clearRecaptchaVerifier();

    const firebaseError = error as { code?: string; message?: string };
    
    if (firebaseError.code === 'auth/invalid-phone-number') {
      throw new Error('Invalid phone number. Please enter a valid 10-digit number.');
    } else if (firebaseError.code === 'auth/too-many-requests') {
      throw new Error('Too many attempts. Please try again later.');
    } else if (firebaseError.code === 'auth/quota-exceeded') {
      throw new Error('SMS quota exceeded. Please try again later.');
    } else if (firebaseError.code === 'auth/captcha-check-failed') {
      throw new Error('Verification failed. Please try again.');
    }

    throw new Error(firebaseError.message || 'Failed to send OTP. Please try again.');
  }
};

/**
 * Verify OTP code
 * @param otp - 6-digit OTP code
 * @returns Firebase User object
 */
export const verifyOTP = async (otp: string): Promise<User> => {
  try {
    if (!confirmationResult) {
      throw new Error('Please request OTP first');
    }

    const result = await confirmationResult.confirm(otp);
    
    confirmationResult = null;
    clearRecaptchaVerifier();
    
    return result.user;
  } catch (error: unknown) {
    const firebaseError = error as { code?: string; message?: string };
    
    if (firebaseError.code === 'auth/invalid-verification-code') {
      throw new Error('Invalid OTP. Please check and try again.');
    } else if (firebaseError.code === 'auth/code-expired') {
      throw new Error('OTP expired. Please request a new one.');
    }

    throw new Error(firebaseError.message || 'Failed to verify OTP. Please try again.');
  }
};

/**
 * Sign out current user
 */
export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
    
    confirmationResult = null;
    clearRecaptchaVerifier();
  } catch (error: unknown) {
    const firebaseError = error as { message?: string };
    throw new Error(firebaseError.message || 'Failed to sign out');
  }
};

/**
 * Get current authenticated user
 * @returns Current user or null
 */
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

/**
 * Subscribe to auth state changes
 * @param callback - Function called on auth state change
 * @returns Unsubscribe function
 */
export const onAuthStateChange = (
  callback: (user: User | null) => void
): (() => void) => {
  return onAuthStateChanged(auth, callback);
};
